import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMembre, updateMembre, createVersement, createDocument, downloadDocumentUrl, getProjets, getParametrage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiArrowLeft, FiDollarSign, FiGrid, FiFileText, FiUser, FiBriefcase, FiEdit2, FiPlus, FiX, FiMapPin, FiFile, FiEye, FiDownload } from 'react-icons/fi';

const formatMoney = (n: number) => new Intl.NumberFormat('fr-FR').format(n) + ' FCFA';
const formatDate = (d: string | null) => {
  if (!d) return '-';
  try {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleDateString('fr-FR');
  } catch { return d; }
};

const MembreDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasModuleWriteAccess } = useAuth();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Refs data
  const [projets, setProjets] = useState<any[]>([]);
  const [typepaiements, setTypepaiements] = useState<any[]>([]);
  const [typedocuments, setTypedocuments] = useState<any[]>([]);
  const [allDirections, setAllDirections] = useState<any[]>([]);

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVersementModal, setShowVersementModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  // Forms
  const [editForm, setEditForm] = useState<any>({});
  const [versementForm, setVersementForm] = useState({ projet_id: '', typepaiement_id: '', dateversement: '', montant: '', reference: '', description: '', mois: '', annee: '' });
  const [documentForm, setDocumentForm] = useState({ libelle: '', typedocument_id: '', datecreation: '', url: '' });
  const [docFile, setDocFile] = useState<File | null>(null);

  const loadData = () => {
    if (!id) return;
    getMembre(id).then(r => setData(r.data)).catch(() => setError('Impossible de charger le membre'));
  };

  useEffect(() => {
    loadData();
    getProjets().then(r => setProjets(r.data)).catch(() => {});
    getParametrage('typepaiements').then(r => setTypepaiements(r.data)).catch(() => {});
    getParametrage('typedocuments').then(r => setTypedocuments(r.data)).catch(() => {});
    getParametrage('directions').then(r => setAllDirections(r.data)).catch(() => {});
  }, [id]);

  const flash = (msg: string, type: 'success' | 'error') => {
    if (type === 'success') { setSuccess(msg); setError(''); } else { setError(msg); setSuccess(''); }
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  if (error && !data) return <div><div className="alert alert-error">{error}</div></div>;
  if (!data) return <div>Chargement...</div>;

  const { user, versements, parcelles, documents, totalVersements } = data;

  // --- Edit Membre ---
  const openEditModal = () => {
    setEditForm({
      prenom: user.prenom || '', nom: user.nom || '', matricule: user.matricule || '',
      email: user.email || '', telephone: user.telephone || '', sexe: user.sexe || '',
      datenaissance: user.datenaissance || '', lieunaissance: user.lieunaissance || '',
      adresse: user.adresse || '', typepiece: user.typepiece || '', cni: user.cni || '',
      direction_id: user.direction_id || '', service: user.service || '',
      corps: user.corps || '', hierarchie: user.hierarchie || ''
    });
    setShowEditModal(true);
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateMembre(id!, editForm);
      flash('Membre mis à jour', 'success');
      setShowEditModal(false);
      loadData();
    } catch (err: any) { flash(err.response?.data?.error || 'Erreur', 'error'); }
  };

  // --- Add Versement ---
  const openVersementModal = () => {
    const now = new Date();
    setVersementForm({ projet_id: '', typepaiement_id: '', dateversement: now.toISOString().split('T')[0], montant: '', reference: '', description: '', mois: String(now.getMonth() + 1), annee: String(now.getFullYear()) });
    setShowVersementModal(true);
  };

  const handleVersementSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createVersement({
        user_id: id, matricule: user.matricule, prenom: user.prenom, nom: user.nom,
        ...versementForm, montant: parseFloat(versementForm.montant) || 0
      });
      flash('Versement ajouté', 'success');
      setShowVersementModal(false);
      loadData();
    } catch (err: any) { flash(err.response?.data?.error || 'Erreur', 'error'); }
  };

  // --- Add Document ---
  const openDocumentModal = () => {
    setDocumentForm({ libelle: '', typedocument_id: '', datecreation: new Date().toISOString().split('T')[0], url: '' });
    setDocFile(null);
    setShowDocumentModal(true);
  };

  const handleDocumentSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (docFile) {
        const fd = new FormData();
        fd.append('fichier', docFile);
        fd.append('user_id', id!);
        Object.entries(documentForm).forEach(([k, v]) => { if (v) fd.append(k, v); });
        await createDocument(fd);
      } else {
        await createDocument({ user_id: id, ...documentForm });
      }
      flash('Document ajouté', 'success');
      setShowDocumentModal(false);
      setDocFile(null);
      loadData();
    } catch (err: any) { flash(err.response?.data?.error || 'Erreur', 'error'); }
  };

  const openDocFile = (doc: any) => {
    const token = localStorage.getItem('chamfb_token');
    window.open(`${downloadDocumentUrl(doc.id)}?token=${token}`, '_blank');
  };

  const downloadDoc = (doc: any) => {
    const token = localStorage.getItem('chamfb_token');
    fetch(downloadDocumentUrl(doc.id), { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.libelle + (doc.extension ? '.' + doc.extension : doc.document?.split('.').pop() ? '.' + doc.document.split('.').pop() : '');
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => flash('Erreur lors du téléchargement', 'error'));
  };


  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button className="btn btn-outline" onClick={() => navigate('/admin/membres')}>
          <FiArrowLeft /> Retour aux membres
        </button>
        {hasModuleWriteAccess('membres') && (
          <button className="btn btn-primary" onClick={openEditModal}><FiEdit2 /> Modifier le membre</button>
        )}
      </div>

      <h1 className="page-title">{user.prenom} {user.nom} {user.matricule ? `(${user.matricule})` : ''}</h1>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div className="card">
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ background: '#e8f5e9', borderRadius: 8, padding: 12 }}><FiDollarSign size={24} color="#2e7d32" /></div>
            <div>
              <div style={{ fontSize: 12, color: '#888' }}>Total versements</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{formatMoney(totalVersements || 0)}</div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ background: '#e3f2fd', borderRadius: 8, padding: 12 }}><FiDollarSign size={24} color="#1565c0" /></div>
            <div>
              <div style={{ fontSize: 12, color: '#888' }}>Nb versements</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{versements?.length || 0}</div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ background: '#e3f2fd', borderRadius: 8, padding: 12 }}><FiGrid size={24} color="#1565c0" /></div>
            <div>
              <div style={{ fontSize: 12, color: '#888' }}>Parcelles</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{parcelles?.length || 0}</div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ background: '#fff3e0', borderRadius: 8, padding: 12 }}><FiFileText size={24} color="#e65100" /></div>
            <div>
              <div style={{ fontSize: 12, color: '#888' }}>Documents</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{documents?.length || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div className="card">
          <div className="card-header"><span><FiUser /> Informations personnelles</span></div>
          <div className="card-body">
            <table className="data-table">
              <tbody>
                <tr><td style={{ width: 160 }}><strong>Prénom</strong></td><td>{user.prenom || '-'}</td></tr>
                <tr><td><strong>Nom</strong></td><td>{user.nom || '-'}</td></tr>
                <tr><td><strong>Sexe</strong></td><td>{user.sexe === 'M' ? 'Masculin' : user.sexe === 'F' ? 'Féminin' : user.sexe || '-'}</td></tr>
                <tr><td><strong>Date de naissance</strong></td><td>{user.datenaissance || '-'}</td></tr>
                <tr><td><strong>Lieu de naissance</strong></td><td>{user.lieunaissance || '-'}</td></tr>
                <tr><td><strong>Adresse</strong></td><td>{user.adresse || '-'}</td></tr>
                <tr><td><strong>Type pièce</strong></td><td>{user.typepiece || '-'}</td></tr>
                <tr><td><strong>N° Pièce (CNI)</strong></td><td>{user.cni || '-'}</td></tr>
                <tr><td><strong>Email</strong></td><td>{user.email || '-'}</td></tr>
                <tr><td><strong>Téléphone</strong></td><td>{user.telephone || '-'}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span><FiBriefcase /> Informations professionnelles</span></div>
          <div className="card-body">
            <table className="data-table">
              <tbody>
                <tr><td style={{ width: 160 }}><strong>Matricule</strong></td><td>{user.matricule || '-'}</td></tr>
                <tr><td><strong>Direction</strong></td><td>{user.direction || '-'}</td></tr>
                <tr><td><strong>Service</strong></td><td>{user.service || '-'}</td></tr>
                <tr><td><strong>Corps</strong></td><td>{user.corps || '-'}</td></tr>
                <tr><td><strong>Hiérarchie</strong></td><td>{user.hierarchie || '-'}</td></tr>
                <tr><td><strong>Rôle</strong></td><td><span className={`badge ${user.role === 'superadmin' ? 'badge-red' : user.role === 'admin_direction' ? 'badge-green' : 'badge-blue'}`}>{user.role}</span></td></tr>
                <tr><td><strong>Statut</strong></td><td><span className={`badge ${user.etat ? 'badge-green' : 'badge-red'}`}>{user.etat ? 'Actif' : 'Inactif'}</span></td></tr>
                <tr><td><strong>Dernière connexion</strong></td><td>{formatDate(user.lastconnexion)}</td></tr>
                <tr><td><strong>Inscription</strong></td><td>{formatDate(user.created)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Versements */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <span>Versements ({versements?.length || 0}) — Total : {formatMoney(totalVersements || 0)}</span>
          {hasModuleWriteAccess('versements') && (
            <button className="btn btn-success btn-sm" onClick={openVersementModal}><FiPlus /> Ajouter un versement</button>
          )}
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr><th>Date</th><th>Type</th><th>Référence</th><th>Mois</th><th>Année</th><th>Description</th><th style={{ textAlign: 'right' }}>Montant</th></tr>
              </thead>
              <tbody>
                {versements && versements.length > 0 ? versements.map((v: any) => (
                  <tr key={v.id}>
                    <td>{formatDate(v.dateversement)}</td>
                    <td>{v.type_libelle || '-'}</td>
                    <td>{v.reference || '-'}</td>
                    <td>{v.mois || '-'}</td>
                    <td>{v.annee || '-'}</td>
                    <td>{v.description || '-'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(v.montant || 0)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 16 }}>Aucun versement</td></tr>
                )}
                {versements && versements.length > 0 && (
                  <tr style={{ fontWeight: 700, background: '#f0f7ff' }}>
                    <td colSpan={6} style={{ textAlign: 'right' }}>Total</td>
                    <td style={{ textAlign: 'right' }}>{formatMoney(totalVersements || 0)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Parcelles */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <span>Parcelles ({parcelles?.length || 0})</span>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr><th>Numéro</th><th>Projet</th><th>Superficie</th><th>Lot</th><th>Nicad</th><th>N° TF</th></tr>
              </thead>
              <tbody>
                {parcelles && parcelles.length > 0 ? parcelles.map((p: any) => (
                  <tr key={p.id}>
                    <td>{p.numero || '-'}</td>
                    <td>{p.projet_libelle || '-'}</td>
                    <td>{p.superficie || '-'}</td>
                    <td>{p.lot || '-'}</td>
                    <td>{p.nicad || '-'}</td>
                    <td>{p.numtf || '-'}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 16 }}>Aucune parcelle</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <span>Documents ({documents?.length || 0})</span>
          {hasModuleWriteAccess('documents') && (
            <button className="btn btn-success btn-sm" onClick={openDocumentModal}><FiPlus /> Ajouter un document</button>
          )}
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr><th>Libellé</th><th>Type</th><th>Date</th><th>Fichier</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {documents && documents.length > 0 ? documents.map((d: any) => {
                  const hasFile = !!d.document;
                  const ext = d.extension || d.document?.split('.').pop() || '';
                  const extLabel = ['pdf'].includes(ext.toLowerCase()) ? 'PDF' : ['jpg','jpeg','png'].includes(ext.toLowerCase()) ? 'IMG' : ext.toUpperCase() || 'FILE';
                  const extColor = ['pdf'].includes(ext.toLowerCase()) ? '#c62828' : ['jpg','jpeg','png'].includes(ext.toLowerCase()) ? '#2e7d32' : '#1565C0';
                  return (
                    <tr key={d.id}>
                      <td><strong>{d.libelle || '-'}</strong></td>
                      <td>{d.type_libelle ? <span className="badge badge-blue">{d.type_libelle}</span> : '-'}</td>
                      <td>{formatDate(d.datecreation || d.created)}</td>
                      <td>
                        {hasFile ? (
                          <span className="badge" style={{ background: extColor, color: '#fff', fontSize: '0.7rem', padding: '2px 6px' }}>{extLabel}</span>
                        ) : '-'}
                      </td>
                      <td>
                        <div className="actions">
                          {hasFile && (
                            <>
                              <button className="btn-icon" style={{ color: '#1565C0' }} onClick={() => openDocFile(d)} title="Visualiser"><FiEye /></button>
                              <button className="btn-icon" style={{ color: '#2e7d32' }} onClick={() => downloadDoc(d)} title="Télécharger"><FiDownload /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 16 }}>Aucun document</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ==================== MODALS ==================== */}

      {/* Modal Edit Membre */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 650 }}>
            <div className="modal-header">
              <h3>Modifier le membre</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleEditSave}>
              <div className="modal-body">
                <h4 style={{ margin: '0 0 12px', color: '#1565C0', borderBottom: '1px solid #e0e0e0', paddingBottom: 6 }}>Identité</h4>
                <div className="form-row">
                  <div className="form-group"><label>Prénom</label><input className="form-input" value={editForm.prenom} onChange={e => setEditForm({ ...editForm, prenom: e.target.value })} required /></div>
                  <div className="form-group"><label>Nom</label><input className="form-input" value={editForm.nom} onChange={e => setEditForm({ ...editForm, nom: e.target.value })} required /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Sexe</label>
                    <select className="form-input" value={editForm.sexe} onChange={e => setEditForm({ ...editForm, sexe: e.target.value })}>
                      <option value="">--</option><option value="M">Masculin</option><option value="F">Féminin</option>
                    </select>
                  </div>
                  <div className="form-group"><label>Date de naissance</label><input className="form-input" value={editForm.datenaissance} onChange={e => setEditForm({ ...editForm, datenaissance: e.target.value })} placeholder="JJ/MM/AAAA" /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Lieu de naissance</label><input className="form-input" value={editForm.lieunaissance} onChange={e => setEditForm({ ...editForm, lieunaissance: e.target.value })} /></div>
                  <div className="form-group"><label>Adresse</label><input className="form-input" value={editForm.adresse} onChange={e => setEditForm({ ...editForm, adresse: e.target.value })} /></div>
                </div>

                <h4 style={{ margin: '16px 0 12px', color: '#1565C0', borderBottom: '1px solid #e0e0e0', paddingBottom: 6 }}>Pièce d'identité</h4>
                <div className="form-row">
                  <div className="form-group"><label>Type de pièce</label>
                    <select className="form-input" value={editForm.typepiece} onChange={e => setEditForm({ ...editForm, typepiece: e.target.value })}>
                      <option value="">--</option><option value="CNI">CNI</option><option value="Passeport">Passeport</option><option value="Permis">Permis</option><option value="Autre">Autre</option>
                    </select>
                  </div>
                  <div className="form-group"><label>N° Pièce</label><input className="form-input" value={editForm.cni} onChange={e => setEditForm({ ...editForm, cni: e.target.value })} /></div>
                </div>

                <h4 style={{ margin: '16px 0 12px', color: '#1565C0', borderBottom: '1px solid #e0e0e0', paddingBottom: 6 }}>Contact</h4>
                <div className="form-row">
                  <div className="form-group"><label>Email</label><input type="email" className="form-input" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} /></div>
                  <div className="form-group"><label>Téléphone</label><input className="form-input" value={editForm.telephone} onChange={e => setEditForm({ ...editForm, telephone: e.target.value })} /></div>
                </div>

                <h4 style={{ margin: '16px 0 12px', color: '#1565C0', borderBottom: '1px solid #e0e0e0', paddingBottom: 6 }}>Professionnel</h4>
                <div className="form-row">
                  <div className="form-group"><label>Matricule</label><input className="form-input" value={editForm.matricule} onChange={e => setEditForm({ ...editForm, matricule: e.target.value })} /></div>
                  <div className="form-group"><label>Corps</label><input className="form-input" value={editForm.corps} onChange={e => setEditForm({ ...editForm, corps: e.target.value })} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Hiérarchie</label><input className="form-input" value={editForm.hierarchie} onChange={e => setEditForm({ ...editForm, hierarchie: e.target.value })} /></div>
                  <div className="form-group"><label>Direction</label>
                    <select className="form-input" value={editForm.direction_id} onChange={e => setEditForm({ ...editForm, direction_id: e.target.value })}>
                      <option value="">--</option>
                      {allDirections.map((d: any) => <option key={d.id} value={d.id}>{d.libelle}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group"><label>Service</label><input className="form-input" value={editForm.service} onChange={e => setEditForm({ ...editForm, service: e.target.value })} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowEditModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Add Versement */}
      {showVersementModal && (
        <div className="modal-overlay" onClick={() => setShowVersementModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 550 }}>
            <div className="modal-header">
              <h3>Ajouter un versement</h3>
              <button className="modal-close" onClick={() => setShowVersementModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleVersementSave}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group"><label>Date *</label><input type="date" className="form-input" value={versementForm.dateversement} onChange={e => setVersementForm({ ...versementForm, dateversement: e.target.value })} required /></div>
                  <div className="form-group"><label>Montant (FCFA) *</label><input type="number" className="form-input" value={versementForm.montant} onChange={e => setVersementForm({ ...versementForm, montant: e.target.value })} required min="0" /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Type de paiement</label>
                    <select className="form-input" value={versementForm.typepaiement_id} onChange={e => setVersementForm({ ...versementForm, typepaiement_id: e.target.value })}>
                      <option value="">--</option>
                      {typepaiements.map(t => <option key={t.id} value={t.id}>{t.libelle}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Projet</label>
                    <select className="form-input" value={versementForm.projet_id} onChange={e => setVersementForm({ ...versementForm, projet_id: e.target.value })}>
                      <option value="">--</option>
                      {projets.map(p => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Mois</label>
                    <select className="form-input" value={versementForm.mois} onChange={e => setVersementForm({ ...versementForm, mois: e.target.value })}>
                      <option value="">--</option>
                      {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'].map((m, i) => (
                        <option key={i+1} value={i+1}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group"><label>Année</label><input type="number" className="form-input" value={versementForm.annee} onChange={e => setVersementForm({ ...versementForm, annee: e.target.value })} min="2020" max="2030" /></div>
                </div>
                <div className="form-group"><label>Référence</label><input className="form-input" value={versementForm.reference} onChange={e => setVersementForm({ ...versementForm, reference: e.target.value })} /></div>
                <div className="form-group"><label>Description</label><input className="form-input" value={versementForm.description} onChange={e => setVersementForm({ ...versementForm, description: e.target.value })} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowVersementModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Ajouter</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Add Document */}
      {showDocumentModal && (
        <div className="modal-overlay" onClick={() => setShowDocumentModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 550 }}>
            <div className="modal-header">
              <h3>Ajouter un document — {user.prenom} {user.nom}</h3>
              <button className="modal-close" onClick={() => setShowDocumentModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleDocumentSave}>
              <div className="modal-body">
                {/* Membre fixé */}
                <div className="selected-membre-card" style={{ marginBottom: 16 }}>
                  <div className="selected-membre-icon"><FiUser /></div>
                  <div className="selected-membre-info">
                    <strong>{user.prenom} {user.nom}</strong>
                    {user.matricule && <small>{user.matricule}</small>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group"><label>Libellé *</label><input className="form-input" value={documentForm.libelle} onChange={e => setDocumentForm({ ...documentForm, libelle: e.target.value })} required /></div>
                  <div className="form-group"><label>Date</label><input type="date" className="form-input" value={documentForm.datecreation} onChange={e => setDocumentForm({ ...documentForm, datecreation: e.target.value })} /></div>
                </div>
                <div className="form-group"><label>Type de document</label>
                  <select className="form-input" value={documentForm.typedocument_id} onChange={e => setDocumentForm({ ...documentForm, typedocument_id: e.target.value })}>
                    <option value="">--</option>
                    {typedocuments.map(t => <option key={t.id} value={t.id}>{t.libelle}</option>)}
                  </select>
                </div>
                {/* File upload */}
                <div className="form-group">
                  <label>Fichier (PDF, Image, Word, Excel)</label>
                  <div className="file-upload-zone" onClick={() => document.getElementById('doc-file-input-detail')?.click()} style={{ padding: '12px 16px', minHeight: 'auto' }}>
                    <input id="doc-file-input-detail" type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" style={{ display: 'none' }}
                      onChange={e => setDocFile(e.target.files?.[0] || null)} />
                    {docFile ? (
                      <div className="file-upload-selected">
                        <FiFile style={{ fontSize: '1.2rem', color: '#1565C0' }} />
                        <div>
                          <strong>{docFile.name}</strong>
                          <small style={{ display: 'block', color: '#888' }}>{(docFile.size / 1024).toFixed(1)} Ko</small>
                        </div>
                        <button type="button" className="btn-icon" onClick={e => { e.stopPropagation(); setDocFile(null); }}><FiX /></button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888' }}>
                        <FiFile /> <span>Cliquer pour sélectionner un fichier</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group"><label>URL (lien externe)</label><input className="form-input" value={documentForm.url} onChange={e => setDocumentForm({ ...documentForm, url: e.target.value })} placeholder="https://..." /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowDocumentModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Ajouter</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembreDetail;
